import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Minimal class boundary. Catches render-time errors anywhere below it and
// shows a recoverable surface — avoids the white-screen-of-death when one
// component throws (e.g. a bad API payload).
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[errorboundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <main className="flex min-h-full items-center justify-center px-8 py-12">
          <div className="w-full max-w-md">
            <h1 className="font-sans text-2xl font-semibold text-graphite-900">
              Something tripped.
            </h1>
            <p className="mt-2 font-sans text-sm text-graphite-700">
              The current screen hit an unhandled error and was unmounted to keep the rest of the
              app responsive.
            </p>
            <pre className="mt-4 max-h-48 overflow-auto rounded-md border border-graphite-100 bg-surface-sunk p-3 font-mono text-[0.7rem] text-graphite-700">
              {this.state.error.message}
            </pre>
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={this.reset}
                className="rounded-md bg-graphite-900 px-3 py-2 font-sans text-sm font-medium text-surface hover:bg-graphite-700"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-md border border-graphite-300 px-3 py-2 font-sans text-sm text-graphite-700 hover:bg-surface-sunk"
              >
                Reload page
              </button>
            </div>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
